/**
 * ## Ours
 * * `Card` — `size` prop (`default` | `sm`); `onClick` adds click-scale animation
 * * `CardImageOverlay` — gradient overlay for image cards; no shadcn equivalent
 *
 * ## Shadcn UI
 * * `Card` — no `size`, no click animation
 * * No `CardImageOverlay`
 */
import * as React from 'react'
import { cn } from '../lib/utils'
import { useClickAnimation } from '../hooks/use-click-animation'
import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'

// FIX: surface-0 looks awful, especially in dark mode.

function Card({
  className,
  size = 'default',
  onClick,
  render,
  ...props
}: useRender.ComponentProps<'div'> & { size?: 'default' | 'sm' }) {
  const clickAnimation = useClickAnimation({ scale: 0.95 })
  const clickable =
    onClick !== undefined ||
    (React.isValidElement(render) && render.type === 'a')

  const defaultProps = {
    'data-slot': 'card',
    'data-size': size,
    onClick,
    className: cn(
      'group/card ring-on-surface/10 bg-surface-0 text-on-surface flex flex-col gap-4 overflow-hidden rounded-[28px] py-4 text-sm ring-1 has-data-[slot=card-footer]:pb-0 has-[>img:first-child]:pt-0 [&_img]:select-none *:[img:first-child]:rounded-t-xl *:[img:last-child]:rounded-b-xl',
      clickable && ['cursor-pointer', clickAnimation.className],
      className,
    ),
  }

  return useRender({
    defaultTagName: 'div',
    render,
    props: clickable
      ? mergeProps(defaultProps, clickAnimation.handlers, props)
      : mergeProps(defaultProps, props),
  })
}

function CardHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        'group/card-header @container/card-header grid auto-rows-min items-start gap-2 rounded-t-xl px-4 has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-4',
        className,
      )}
      {...props}
    />
  )
}

function CardTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        'text-base leading-snug font-medium group-data-[size=sm]/card:text-sm',
        className,
      )}
      {...props}
    />
  )
}

function CardDescription({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-description"
      className={cn('text-on-surface-secondary text-sm', className)}
      {...props}
    />
  )
}

function CardAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        'col-start-2 row-span-2 row-start-1 self-start justify-self-end',
        className,
      )}
      {...props}
    />
  )
}

function CardContent({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-content"
      className={cn('px-4', className)}
      {...props}
    />
  )
}

function CardFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-footer"
      className={cn('flex items-center rounded-b-xl p-4', className)}
      {...props}
    />
  )
}

function CardImageOverlay({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="card-image-overlay"
      className={cn(
        'absolute inset-x-0 bottom-0 isolate z-[3] px-4 pt-10 pb-4',
        "after:absolute after:inset-0 after:z-[-1] after:rounded-[inherit] after:mix-blend-multiply after:content-[''] after:[background:linear-gradient(to_bottom,transparent_0%,rgba(0,0,0,0.6)_66%)]",
        className,
      )}
      {...props}
    />
  )
}

Card.renderable = true

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardAction,
  CardDescription,
  CardContent,
  CardImageOverlay,
}
